import { routes } from 'vue-router/auto-routes'
import { createRouter, createWebHistory } from 'vue-router'
import { checkFeatureFlagAccessGuard } from './router/featureFlagAccessGuards'
import type {
  NavigationGuard,
  NavigationGuardWithThis,
  RouteLocationNormalized,
  RouteRecordRaw,
} from 'vue-router'
import type { IViewAsHeaders } from '~/stores/auth'
import { DESELECT_SERVICE_LINES_PATHS } from '~/constants/navigation'
import { AuthStatus, useAuthStore } from '~/stores/auth'
import { NAME, PATH } from '~/constants/strings'
import { useNavigationStore } from '~/stores/navigation'
import { combinePathAndQueryStrings } from '~/helpers'
import NotFoundPage from '~/components/Common/NotFoundPage.vue'
import {
  checkAAPSummariesAccessGuard,
  checkAdminSupportAccessGuard,
  checkClientManagementAccessGuard,
  checkClientSupportAccessGuard,
  checkDatavizAccessGuard,
  checkExecutiveSummaryAccessGuard,
  checkFeatureFlagManagementAccessGuard,
  checkManageSecurityAccessGuard,
  checkManageSystemAlertsAccessGuard,
  checkReportsManagementAccessGuard,
  checkReportsPageAccessGuard,
  checkResourceLibraryAccessGuard,
  checkServiceDeliverableManageOnePageAccessGuard,
  checkServiceDeliverablePageAndManageAllAccessGuard,
  checkUserManagementAccessGuard,
} from '~/modules/router/pageAccessGuards'
import { getViewAsUserQueryValue } from '~/api/instance'
import { notifyFailure } from '~/utils/notifications'
import { getServiceNameForPaths } from '~/helpers/serviceLine'

const rootRoute: RouteRecordRaw = {
  path: PATH.ROOT,
  beforeEnter: (_to, _from, next) => {
    // Redirect to login
    next(PATH.LOGIN)
  },
  meta: {
    title: 'Berkshire Client Portal',
  },
}

const router = createRouter({
  routes: [
    ...routes,
    rootRoute,
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: NotFoundPage,
      meta: { title: 'Page Not Found' },
    },
  ],
  history: createWebHistory(),
})

const checkQueryParam = (route: RouteLocationNormalized, paramName: string) => {
  // First, check if the query param exists
  if (route.query[paramName]) {
    return true
  }

  // If not found in query params, check the full path
  const url = new URL(route.fullPath, window.location.origin)
  return url.searchParams.has(paramName)
}

// Navigation Guards
const requiredLoggedInGuard: NavigationGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
) => {
  const getStartingPath = () => {
    let startingPath

    if (isSubdomainRedirection || isViewAsRedirection) {
      startingPath = originalPath
    } else {
      startingPath = combinePathAndQueryStrings(to.path, to.query)
    }

    // prevent trying to autologin to /login page
    // prevents going back to password expired page after changing password
    // prevents going back to welcome page after starting up
    if (
      [PATH.LOGIN, PATH.PASSWORD_EXPIRED, PATH.WELCOME].includes(startingPath)
    ) {
      startingPath = undefined
    }

    return startingPath
  }

  const AuthStore = useAuthStore()
  const isSubdomainRedirection = checkQueryParam(to, 'subdomainRedirect')
  const isViewAsRedirection = checkQueryParam(to, 'viewAsUser')
  const intendedPath = to.fullPath // Store the full path including the query and hash
  const originalPath = to.query.originalPath
    ? decodeURIComponent(to.query.originalPath as string)
    : undefined

  // Handling logout
  const isLoggingOut = (): boolean => {
    return [
      AuthStatus.InitiatingLoggingOut,
      AuthStatus.UserLoggedOut,
      AuthStatus.SystemLoggedOut,
    ].includes(AuthStore.authStatus)
  }
  const loginRoutes = [PATH.LOGIN, PATH.LOGIN_MFA]
  const isMFALogin = (): boolean => {
    return (
      AuthStore.authStatus === AuthStatus.InitiatingLoginMfa &&
      loginRoutes.includes(to.path)
    )
  }
  const isLoggedInAuthStatus = (): boolean =>
    AuthStore.authStatus === AuthStatus.LoggedIn
  const isInitializingSession = (): boolean => {
    const isInitializingSessionStatuses = [
      AuthStatus.InitiatingUserSession,
      AuthStatus.UserLoaded,
    ]

    return isInitializingSessionStatuses.includes(AuthStore.authStatus)
  }

  if (isLoggingOut()) {
    // Ensure the user is redirected to the login page and does not loop back to a protected route
    if (to.path !== PATH.LOGIN) {
      return { path: PATH.LOGIN }
    }
    return true
  }

  if (isMFALogin()) {
    return true
  }

  // Check if the user is already logged in
  if (isLoggedInAuthStatus()) {
    // if the app navigates to login page when logged in go to first path

    if (to.path === PATH.LOGIN) {
      if (AuthStore.firstPath && from.path !== AuthStore.firstPath) {
        return { path: AuthStore.firstPath }
      }
      AuthStore.firstPath = ''
      AuthStore.authStatus = AuthStatus.InitiatingLogin
      return { path: PATH.LOGIN }
    }

    // do nothing user is already logged in
    return true
  }

  // make sure not running initialize session multiple times
  if (isInitializingSession()) {
    return true
  }

  // Check if user has a valid session
  const hasCookie = await AuthStore.checkValidCookie()
  if (hasCookie?.success) {
    AuthStore.defaultClientSubdomain = hasCookie.subdomain
    const loginRoutes = [PATH.LOGIN, PATH.LOGIN_MFA, PATH.WELCOME]
    if (to.meta.requiresAuth || loginRoutes.includes(to.path)) {
      await AuthStore.redirectToNewSubdomainIfNecessary({
        targetSubdomain: hasCookie.subdomain,
      })
    }

    const { isLoggedIn, user, avatar } = await AuthStore.getUserInformation()
    if (isLoggedIn) {
      AuthStore.authStatus = AuthStatus.Authenticated
    }

    if (
      isLoggedIn &&
      user?.emailAddress &&
      AuthStore.authStatus === AuthStatus.Authenticated
    ) {
      AuthStore.user = user
      AuthStore.email = user.emailAddress
      AuthStore.convertAvatarBlob(avatar as Blob)
      AuthStore.authStatus = AuthStatus.UserLoaded

      const startingPath = getStartingPath()

      AuthStore.initializeUserSession(startingPath)
      return true
    }

    // If route requires authentication and user is not logged in, redirect to login
    if (to.meta.requiresAuth && !isLoggedIn) {
      // Check if the intended path is root ('/') and skip adding the redirect parameter
      if (intendedPath !== PATH.ROOT) {
        const loginPathWithRedirect = {
          path: PATH.LOGIN,
          query: {
            redirect: encodeURIComponent(intendedPath),
          },
        }
        return loginPathWithRedirect
      }

      // If the intended path is root, simply redirect to login without the redirect parameter
      return { path: PATH.LOGIN }
    }

    // If the user tries to access the login page while not logged in, allow
    if (to.path === PATH.LOGIN && !isLoggedIn) {
      return true
    }
  }

  if (!hasCookie?.success && to.meta.requiresAuth) {
    // If no valid cookie is present, check if the route requires auth and the user is not logged in
    // Check if the intended path is root ('/') and skip adding the redirect parameter
    if (intendedPath !== '/') {
      const isTimeout = checkQueryParam(to, 'timeout')
      const currentUrl = new URL(to.fullPath, window.location.origin)
      if (isTimeout) {
        currentUrl.searchParams.delete('timeout')
      }
      const redirectUrl = encodeURIComponent(
        currentUrl.pathname + currentUrl.search
      )
      // Including 'timeout=true' only if 'isTimeout' is true
      const query = {
        redirect: redirectUrl,
        ...(isTimeout && { timeout: 'true' }),
      }
      return {
        path: PATH.LOGIN,
        query,
      }
    }
    // If the intended path is root, simply redirect to login without the redirect parameter
    return { path: PATH.LOGIN }
  }

  // If none of the above, continue with the navigation
  return true
}

const removeSelectedTopNavigationServiceIdGuard = (
  to: RouteLocationNormalized
) => {
  if (DESELECT_SERVICE_LINES_PATHS.includes(to.path)) {
    const NavigationStore = useNavigationStore()
    NavigationStore.selectedTopNavigationServiceId = ''
  }
}

const addTopNavigationServiceIdGuard = async (to: RouteLocationNormalized) => {
  const NavigationStore = useNavigationStore()
  if (
    !DESELECT_SERVICE_LINES_PATHS.includes(to.path) &&
    NavigationStore.selectedTopNavigationServiceId === ''
  ) {
    const serviceLineFromPath = NavigationStore.findServiceLine(to.path)
    NavigationStore.selectedTopNavigationServiceId =
      serviceLineFromPath?.serviceID ?? ''
  }
}

const removeMfaLoginFromHistoryGuard: NavigationGuardWithThis<undefined> = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
) => {
  const inANavigationLoop = to.redirectedFrom?.path === to.path
  if (from.path === PATH.LOGIN_MFA && !inANavigationLoop) {
    // remove from history
    return {
      ...to,
      replace: true,
    }
  }
  return true
}

const redirectToLoginIfAtRoot: NavigationGuard = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next
) => {
  if (to.path === PATH.ROOT) {
    next(PATH.LOGIN)
  } else {
    next()
  }
}

// Redirect for users with old email that has a link to dataviz
const redirectOldDatavizGuard: NavigationGuard = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next
) => {
  if (to.path === '/data-viz/overview') {
    next({
      path: '/compliance/data-viz/overview',
      query: to.query,
    })
  } else {
    next()
  }
}

const redirectOldReportSummariesGuard: NavigationGuard = (to, _from, next) => {
  const previousReportSummaryPaths = [
    '/503-vevraa/503-vevraa-summaries',
    '/aap/aap-summaries',
  ]

  if (previousReportSummaryPaths.includes(to.path)) {
    const serviceLineParam = to.params.serviceLine
      ? to.params.serviceLine
      : to.params.pathMatch[0]

    return next({
      name: NAME.SUMMARIES,
      params: { serviceLine: serviceLineParam },
    })
  }
  return next()
}

const handleViewingAs: NavigationGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next
) => {
  const AuthStore = useAuthStore()
  const viewAsUserQueryValue = getViewAsUserQueryValue() || ''
  if (viewAsUserQueryValue) {
    const restrictedViewAsPaths = [
      PATH.ADMIN_SUPPORT_ACCESS,
      PATH.CLIENT_MANAGEMENT,
      PATH.CLIENT_SUPPORT_ACCESS,
      PATH.CONTACT_SUPPORT,
      PATH.CREATE_USER,
      PATH.DOCUMENT_CENTER,
      PATH.DOCUMENT_CENTER_PUBLIC_UPLOAD,
      PATH.MANAGE_CLIENT_INFORMATION,
      PATH.MANAGE_SYSTEM_ALERTS,
      PATH.RESOURCE_LIBRARY,
      PATH.RESOURCE_LIBRARY_PREVIEW,
      PATH.SECURITY_SETTINGS,
      PATH.USER_MANAGEMENT,
      PATH.USER_PROFILE,
    ]
    const isVisitingRestrictedViewAsPath = restrictedViewAsPaths.includes(
      to.path
    )
    if (isVisitingRestrictedViewAsPath) {
      next({
        path: '/',
        query: { viewAsUser: viewAsUserQueryValue, subdomainRedirect: 'true' },
      })
      return
    }
  }
  // Set view as header if it doesn't exist
  if (
    !(instance.defaults.headers as IViewAsHeaders)['X-View-As'] &&
    viewAsUserQueryValue
  ) {
    ;(instance.defaults.headers as IViewAsHeaders)['X-View-As'] =
      viewAsUserQueryValue
    AuthStore.isViewAsUser = true
  }
  if (to.path === PATH.LOGIN) {
    next()
  } else {
    const viewAsUser = from.query.viewAsUser
    // Check if the current route already has the viewAsUser query parameter set correctly
    const isViewAsQuerySet = to.query.viewAsUser === viewAsUser
    if (viewAsUser && !isViewAsQuerySet) {
      next({ path: to.path, query: { ...to.query, viewAsUser } }) // Ensure other query params are preserved
    } else {
      next()
    }
  }
}

const handleRouteTitleCheck: NavigationGuard = (to) => {
  const leaf = to.matched[to.matched.length - 1]

  if (import.meta.env.DEV && leaf && !leaf.meta.title) {
    console.log({ leaf, to })
    const message = `❌ Missing meta.title on route path:${to.fullPath}, name: ${to.name}`
    notifyFailure(message)
    throw new Error(message)
  }
}

const redirectOldServiceLineGuard: NavigationGuard = (to) => {
  if (!('serviceLine' in to.params)) {
    return
  }

  const NavigationStore = useNavigationStore()
  const serviceLine = NavigationStore.findServiceLine(to.path)

  if (!serviceLine?.serviceID) {
    return
  }

  const serviceNameForPaths = getServiceNameForPaths(serviceLine.serviceType)
  if (serviceNameForPaths !== to.params.serviceLine) {
    return {
      ...to,

      replace: true,

      params: {
        ...to.params,
        serviceLine: serviceNameForPaths,
      },
    }
  }
}

// * Navigation Guards

// - A11Y/SEO
router.beforeEach(handleRouteTitleCheck)

// - Login Related
router.beforeEach(redirectToLoginIfAtRoot)
router.beforeEach(handleViewingAs)
router.beforeEach(removeMfaLoginFromHistoryGuard)
router.beforeEach(requiredLoggedInGuard)

// - Page Access
router.beforeEach(checkFeatureFlagAccessGuard)
router.beforeEach(checkUserManagementAccessGuard)
router.beforeEach(checkClientManagementAccessGuard)
router.beforeEach(checkDatavizAccessGuard)
router.beforeEach(checkResourceLibraryAccessGuard)
router.beforeEach(checkServiceDeliverableManageOnePageAccessGuard)
router.beforeEach(checkServiceDeliverablePageAndManageAllAccessGuard)
router.beforeEach(checkManageSecurityAccessGuard)
router.beforeEach(checkManageSystemAlertsAccessGuard)
router.beforeEach(checkReportsPageAccessGuard)
router.beforeEach(checkReportsManagementAccessGuard)
router.beforeEach(checkAAPSummariesAccessGuard)
router.beforeEach(checkAdminSupportAccessGuard)
router.beforeEach(checkClientSupportAccessGuard)
router.beforeEach(checkExecutiveSummaryAccessGuard)
router.beforeEach(checkFeatureFlagManagementAccessGuard)

// - Service Line Handling
router.beforeEach(redirectOldServiceLineGuard)
router.beforeEach(removeSelectedTopNavigationServiceIdGuard)
router.beforeEach(addTopNavigationServiceIdGuard)

// - Redirect old links
router.beforeEach(redirectOldDatavizGuard)
router.beforeEach(redirectOldReportSummariesGuard)

export default router
